import React, { useState, useCallback, useRef, useEffect } from "react";
import { FaSms } from "react-icons/fa";
import { usePersonnel } from "../../hooks/usePersonnel";
import { useUnits } from "../../hooks/useUnits";
import { useInventory } from "../../hooks/useInventory";
import { useItems } from "../../hooks/useItems";
import { useAuth } from "../../hooks/useAuth";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useKits } from "../../hooks/useKits";
import { getOrCreatePageId } from "../../utils/employeePages";
import { getAssignmentUrl } from "../../config/urls";
import {
  sendTsavReportSms,
  getTsavDailyUsageShared,
  incrementTsavDailyUsageShared,
  hasSentTsavFromSignatureToday,
  markTsavSentFromSignatureToday,
} from "../../services/smsService";
import SignatureTabContent from "./SignatureTabContent";
import SignatureTabBar from "./components/SignatureTabBar";
import "./Signature.css";
import "./MobileSignature.css";

const MAX_TABS = 5;

const Signature = () => {
  const { personnel, loading, error } = usePersonnel();
  const { units } = useUnits();
  const {
    inventory,
    addInventoryItem,
    updateInventoryItem,
    refreshInventory,
  } = useInventory();
  const { items } = useItems();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { kits, addKit, updateKit, deleteKit, reorderKits } = useKits();
  const isChildUser = user?.permissions === "childUser";

  const [tsavSending, setTsavSending] = useState(false);
  const [tsavDailyCount, setTsavDailyCount] = useState(0);
  /** tabId -> selected soldier personalNumber (trimmed), for Tsav button enablement */
  const [tabSelectedPersonalNumber, setTabSelectedPersonalNumber] = useState(
    {},
  );

  // Tab management state
  const [tabs, setTabs] = useState([
    { id: 1, label: "חייל חדש", hasContent: false },
  ]);
  const [activeTabId, setActiveTabId] = useState(1);
  const [mountedTabIds, setMountedTabIds] = useState(() => new Set([1]));
  const nextTabId = useRef(2);
  const tabRefs = useRef({});

  useEffect(() => {
    if (isChildUser) return;
    getTsavDailyUsageShared().then((c) => setTsavDailyCount(c));
  }, [isChildUser]);

  const addTab = useCallback(() => {
    if (tabs.length >= MAX_TABS) return;
    const id = nextTabId.current++;
    setTabs((prev) => [...prev, { id, label: "חייל חדש", hasContent: false }]);
    setMountedTabIds((prev) => new Set([...prev, id]));
    setActiveTabId(id);
  }, [tabs.length]);

  const closeTab = useCallback(
    (tabId) => {
      if (tabs.length === 1) {
        // Last tab: reset it instead of closing
        tabRefs.current[tabId]?.current?.reset();
        setTabs([{ id: tabId, label: "חייל חדש", hasContent: false }]);
        return;
      }
      const idx = tabs.findIndex((t) => t.id === tabId);
      const newTabs = tabs.filter((t) => t.id !== tabId);
      setTabs(newTabs);
      setTabSelectedPersonalNumber((prev) => {
        if (!(tabId in prev)) return prev;
        const next = { ...prev };
        delete next[tabId];
        return next;
      });
      setMountedTabIds((prev) => {
        const n = new Set(prev);
        n.delete(tabId);
        return n;
      });
      delete tabRefs.current[tabId];
      if (tabId === activeTabId) {
        setActiveTabId(newTabs[Math.max(0, idx - 1)].id);
      }
    },
    [tabs, activeTabId],
  );

  // CRITICAL: This function must remain wrapped in useCallback.
  // It is passed to SignatureTabContent and used inside a useEffect dependency array.
  // Creating a new reference on every render (e.g., using an inline arrow function) will cause an infinite render loop (Maximum update depth exceeded).
  const handleTabStateChange = useCallback(
    (tabId, { employeeName, hasContent, personalNumber }) => {
      const nextLabel = employeeName || "חייל חדש";
      const pn =
        personalNumber != null && String(personalNumber).trim() !== ""
          ? String(personalNumber).trim()
          : "";
      setTabSelectedPersonalNumber((prev) => {
        if (prev[tabId] === pn) return prev;
        return { ...prev, [tabId]: pn };
      });
      setTabs((prev) => {
        const target = prev.find((t) => t.id === tabId);
        if (
          target &&
          target.label === nextLabel &&
          target.hasContent === hasContent
        ) {
          return prev;
        }
        return prev.map((t) =>
          t.id === tabId ? { ...t, label: nextLabel, hasContent } : t,
        );
      });
    },
    [],
  );

  const tsavSenderKey = user?.email || user?.id || "";
  const activeTabPersonalNumber =
    tabSelectedPersonalNumber[activeTabId] || "";
  const tsavAlreadySentToActiveToday =
    !!activeTabPersonalNumber &&
    hasSentTsavFromSignatureToday(tsavSenderKey, activeTabPersonalNumber);

  const handleSendTsavToSelectedSoldier = useCallback(async () => {
    if (isChildUser) return;
    const currentCount = await getTsavDailyUsageShared();
    if (currentCount >= 2) {
      alert(
        "הגעת למגבלת 2 שליחות ביום לכלל המשתמשים. ניתן לשלוח שוב מחר.",
      );
      setTsavDailyCount(currentCount);
      return;
    }

    const emp = tabRefs.current[activeTabId]?.current?.getSelectedEmployee?.();
    const pn = emp?.personalNumber != null ? String(emp.personalNumber).trim() : "";
    if (!pn) {
      alert("בחר חייל לשליחת הודעת דוח צ'.");
      return;
    }
    if (hasSentTsavFromSignatureToday(tsavSenderKey, pn)) {
      alert("כבר נשלחה היום הודעת דוח צ' לחייל זה ממסך החתימה.");
      return;
    }

    const fullName =
      `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || pn;
    if (
      !window.confirm(
        `לשלוח SMS דוח צ' ל-${fullName}?`,
      )
    ) {
      return;
    }
    if (!emp.phone) {
      alert("אין מספר טלפון לחייל זה.");
      return;
    }

    setTsavSending(true);
    try {
      const pageId = await getOrCreatePageId(
        emp.id,
        pn,
        user?.email || "",
      );
      const url = getAssignmentUrl(pageId);
      const result = await sendTsavReportSms(emp.phone, fullName, url);
      if (!result?.success) {
        alert(result?.error || "שגיאה בשליחת SMS");
        return;
      }
      markTsavSentFromSignatureToday(tsavSenderKey, pn);
      await incrementTsavDailyUsageShared();
      setTsavDailyCount((c) => c + 1);
      alert("הודעת דוח צ' נשלחה.");
    } catch (e) {
      console.error("[Tsav SMS signature]", e);
      alert("שגיאה בשליחת SMS");
    } finally {
      setTsavSending(false);
    }
  }, [isChildUser, activeTabId, user?.email, tsavSenderKey]);

  if (loading) {
    return (
      <div
        id="signature-page-root"
        className="signature-container signature-page-mobile-scope signature-page-scope"
      >
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>טוען נתוני חיילים...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        id="signature-page-root"
        className="signature-container signature-page-mobile-scope signature-page-scope"
      >
        <div className="error-container">
          <p>שגיאה בטעינת נתוני החיילים: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      id="signature-page-root"
      className="signature-container signature-page-mobile-scope signature-page-scope"
    >
      <div className="signature-page-top">
        <div className="signature-header">
          <div className="signature-header-title-row">
            <h1>חתימה/זיכוי</h1>
            {!isChildUser && (
              <button
                type="button"
                className="sms-header-icon-btn"
                onClick={handleSendTsavToSelectedSoldier}
                disabled={
                  tsavSending ||
                  tsavDailyCount >= 2 ||
                  !activeTabPersonalNumber ||
                  tsavAlreadySentToActiveToday
                }
                title="שלח SMS דוח צ לחייל הנבחר בלשונית הנוכחית"
              >
                <FaSms aria-hidden />
              </button>
            )}
          </div>
        </div>
        <SignatureTabBar
          tabs={tabs}
          activeTabId={activeTabId}
          onTabSelect={setActiveTabId}
          onTabClose={closeTab}
          onAddTab={addTab}
          maxTabs={MAX_TABS}
        />
      </div>

      {[...mountedTabIds].map((tabId) => {
        if (!tabRefs.current[tabId]) {
          tabRefs.current[tabId] = React.createRef();
        }
        return (
          <div
            key={tabId}
            style={{ display: tabId === activeTabId ? "block" : "none" }}
          >
            {/* WARNING: Pass the stable callback and tabId as separate props.
              DO NOT use an inline wrapper here like: onTabStateChange={(state) => handleTabStateChange(tabId, state)}
            */}
            <SignatureTabContent
              ref={tabRefs.current[tabId]}
              tabId={tabId}
              inventory={inventory}
              addInventoryItem={addInventoryItem}
              updateInventoryItem={updateInventoryItem}
              refreshInventory={refreshInventory}
              items={items}
              units={units}
              personnel={personnel}
              kits={kits}
              addKit={addKit}
              updateKit={updateKit}
              deleteKit={deleteKit}
              reorderKits={reorderKits}
              user={user}
              isMobile={isMobile}
              onTabStateChange={handleTabStateChange}
            />
          </div>
        );
      })}
    </div>
  );
};

export default Signature;
